import type { ParameterTimeSeriesConfiguration } from "@/lib/api";
import { defaultVisualizationForUnit } from "@/features/shared/widgets/model-map/model-map-visualization-types";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { sharesPropertyKind } from "@/lib/parameters/parameter-units.utils";

export function isParameterInTimeSeriesConfigurations(
  parameterId: string,
  configurations: ParameterTimeSeriesConfiguration[],
): boolean {
  return configurations.some((config) =>
    config.series.some((series) => series.parameter_id === parameterId),
  );
}

export function unitCodeForTimeSeriesConfiguration(
  config: ParameterTimeSeriesConfiguration,
): string | undefined {
  return config.unit?.code;
}

export function findTimeSeriesConfigIndexForUnit(
  configurations: ParameterTimeSeriesConfiguration[],
  unitCode: string | undefined,
): number | null {
  if (!unitCode) return null;
  const index = configurations.findIndex(
    (config) =>
      config.series.length > 0 && unitCodeForTimeSeriesConfiguration(config) === unitCode,
  );
  return index >= 0 ? index : null;
}

export function findCompatibleTimeSeriesConfigIndex(
  parameterId: string,
  configurations: ParameterTimeSeriesConfiguration[],
  parameters: TimeSeriesParameterOption[],
): number | null {
  const parameter = parameters.find((candidate) => candidate.id === parameterId);
  if (!parameter) return null;

  const index = configurations.findIndex((config) => {
    if (config.series.length === 0 || !config.unit?.code) return false;
    // Groupable iff every existing series measures the same quantity as the
    // marker parameter; the group's shared unit then applies to all of them.
    return config.series.every((series) => {
      const seriesParameter = parameters.find(
        (candidate) => candidate.id === series.parameter_id,
      );
      return seriesParameter ? sharesPropertyKind(seriesParameter, parameter) : false;
    });
  });

  return index >= 0 ? index : null;
}

/**
 * Ensures a marker parameter is represented in time-series configuration.
 * Adds to an existing group with a compatible unit, or creates an untitled new group.
 */
export function applyTimeSeriesFixForParameter(
  parameterId: string,
  configurations: ParameterTimeSeriesConfiguration[],
  parameters: TimeSeriesParameterOption[],
  untitledGroupTitle: string,
): ParameterTimeSeriesConfiguration[] {
  if (isParameterInTimeSeriesConfigurations(parameterId, configurations)) {
    return configurations;
  }

  const parameter = parameters.find((p) => p.id === parameterId);
  if (!parameter) return configurations;

  const existingIndex = findCompatibleTimeSeriesConfigIndex(
    parameterId,
    configurations,
    parameters,
  );

  if (existingIndex !== null) {
    const group = configurations[existingIndex];
    return configurations.map((config, index) =>
      index === existingIndex
        ? {
            ...group,
            series: [
              ...group.series,
              { parameter_id: parameterId, label: parameter.label },
            ],
          }
        : config,
    );
  }

  const defaultUnit = parameter.units[0];
  if (!defaultUnit) return configurations;

  return [
    ...configurations,
    {
      title: untitledGroupTitle,
      visualization_type: defaultVisualizationForUnit(defaultUnit.code, [parameterId]),
      unit: defaultUnit,
      series: [{ parameter_id: parameterId, label: parameter.label }],
    },
  ];
}
